import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Box, IconButton, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { CanvasContext } from '@/designs-dev/handover/Canvas';

// How far one press or one click moves the strip: most of a screenful, with a
// sliver of the old view left behind so the eye has something to land on.
const STEP = 0.8;

/**
 * A row of minis you can move without a trackpad.
 *
 * A flow is wider than the window on purpose, and the only way to move it used
 * to be a two-finger swipe. On a mouse a wheel is vertical, so anybody without
 * a trackpad could see the first four steps of a flow and had no way to reach
 * the fifth. Three ways in now, none of which have to be discovered in a
 * particular order:
 *
 * - the chevrons, which appear at whichever end there is more to see
 * - left and right arrows, once the strip has focus, with Home and End for the
 *   ends of it
 * - shift and the wheel, which the browser has always done and nobody knows
 *
 * The plain wheel is deliberately left alone. Turning it into sideways movement
 * is the usual trick and it is wrong here: this page is a column of these, so
 * hijacking the wheel would mean the page stopped scrolling every time the
 * pointer crossed a flow.
 */
const ScrollRow = ({
  title,
  children,
}: {
  /** The flow's name. Rendered here so the controls have a line to sit on. */
  title?: string;
  children: ReactNode;
}) => {
  const canvas = useContext(CanvasContext);
  const ref = useRef<HTMLDivElement>(null);
  const [room, setRoom] = useState({ before: false, after: false });

  // Which chevrons are worth drawing. Recomputed on scroll and on resize,
  // because a flow narrower than the window needs neither and a window dragged
  // narrower needs both.
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setRoom({
      before: el.scrollLeft > 1,
      after: max > 1 && el.scrollLeft < max - 1,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [measure]);

  const move = (direction: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: direction * el.clientWidth * STEP,
      behavior: 'smooth',
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const el = ref.current;
    if (!el) return;
    const to = (left: number) => {
      e.preventDefault();
      el.scrollTo({ left, behavior: 'smooth' });
    };
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      to(0);
    } else if (e.key === 'End') {
      to(el.scrollWidth);
    }
  };

  // On the title's line rather than floating over the row. Pinned to the middle
  // of the strip is where these normally go, and it does not work here: a flow
  // with alts and readmes under it is tall, so the middle of it is empty space
  // a long way below the screens the buttons move. Up here they are in the same
  // place on every flow, and they cover nothing.
  const chevron = (side: 'left' | 'right') => (
    <IconButton
      aria-label={side === 'left' ? 'Scroll left' : 'Scroll right'}
      onClick={() => move(side === 'left' ? -1 : 1)}
      disabled={side === 'left' ? !room.before : !room.after}
      size='small'
    >
      <Icon
        icon={side === 'left' ? 'chevron-left' : 'chevron-right'}
        fontSize={14}
      />
    </IconButton>
  );

  // On a canvas the page is what moves, so the row runs its full width and
  // has nothing of its own to scroll.
  if (canvas) {
    return (
      <Box>
        {title && (
          <Typography sx={{ pb: 1 }} variant='h4'>
            {title}
          </Typography>
        )}
        {children}
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        sx={{ alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        direction='row'
        spacing={2}
      >
        {title ? <Typography variant='h4'>{title}</Typography> : <Box />}
        {/* Only where there is anywhere to go. A flow that fits shows nothing,
            rather than a pair of dead buttons saying it might not. */}
        {(room.before || room.after) && (
          <Stack direction='row' spacing={0.5}>
            {chevron('left')}
            {chevron('right')}
          </Stack>
        )}
      </Stack>
      <Box
        ref={ref}
        onScroll={measure}
        onKeyDown={onKeyDown}
        // Focusable so the arrow keys have somewhere to land. A scrollable box
        // is a legitimate tab stop: there is content in it you cannot otherwise
        // reach from the keyboard.
        tabIndex={0}
        role='group'
        aria-label='Flow steps'
        sx={{
          pb: 1,
          overflowX: 'auto',
          // The outline would otherwise trace the full scroll width and hang
          // off the side of the window.
          outlineOffset: '-2px',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ScrollRow;
